'use client';

import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import Tooltip from '@/components/Tooltip';
import { EPlatform } from '@/utils/enums/platform-enum';
import { QuestionFilled, QuestionOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface AnniCountDownProps {
  time: number;
  preOrAfter?: 'pre' | 'after';
  intro?: string;
  onCountDownEnd?: () => void;
  onChangeAfterAnniGreaterFlag?: (flag: boolean) => void;
}

export default function AnniCountDown({
  time,
  preOrAfter = 'pre',
  intro = '',
  onCountDownEnd,
  onChangeAfterAnniGreaterFlag,
}: AnniCountDownProps) {
  const t = useTranslations('components.anniCountDown');
  const { deviceType } = useContext(ResponsiveContext);
  const [day, setDay] = useState(0);
  const [hr, setHr] = useState('00');
  const [min, setMin] = useState('00');
  const [sec, setSec] = useState('00');
  const [timeLeft, setTimeLeft] = useState(0);
  const [showIntro, setShowIntro] = useState(false);
  const [lastDayBeforeEnd, setLastDayBeforeEnd] = useState(0); // 记录倒计时结束前的最后天数
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false); // 用于标记是否已经初始化

  // 计算倒计时结束后的类型（基于倒计时结束前的最后天数）
  const typeDownClassAfterEnd = lastDayBeforeEnd >= 3 ? 'GreaterThanThree' : 'LessThanThree';

  // 防止跟着倒计时组件不停闪动。
  const HelpTooltip = useMemo(() => {
    const sx = {
      '& .MuiTooltip-tooltip': {
        maxWidth: '300px',
        fontSize: '14px',
        padding: '8px 12px',
        whiteSpace: 'pre-line',
        fontFamily: 'var(--font-bricolage)',
      },
    } as const;

    return memo(({ title, colorClass }: { title: string; colorClass: string }) => (
      <Tooltip title={title} placement="bottom" variant="light" sx={sx}>
        <QuestionFilled
          className={`ml-[2px] text-[12px] md:text-[14px] ${colorClass} cursor-pointer`}
        />
      </Tooltip>
    ));
  }, [intro]);

  // 处理倒计时逻辑
  const handleCountTime = (val: number) => {
    setTimeLeft(val);

    if (val < 0) {
      setDay(0);
      setHr('00');
      setMin('00');
      setSec('00');
      return;
    }

    const dayNum = Math.floor(val / (60 * 60 * 24));
    const hrNum = Math.floor(val / (60 * 60)); // 总小时数，不取模
    const minNum = Math.floor((val / 60) % 60);
    const secNum = Math.floor(val % 60);

    setDay(dayNum);
    setHr(hrNum > 9 ? hrNum.toString() : `0${hrNum}`);
    setMin(minNum > 9 ? minNum.toString() : `0${minNum}`);
    setSec(secNum > 9 ? secNum.toString() : `0${secNum}`);

    // 持续更新最后的天数（只要倒计时还在进行）
    setLastDayBeforeEnd(dayNum);

    timerRef.current = setTimeout(() => {
      handleCountTime(val - 1);
    }, 1000);
  };

  // 监听day变化，触发afterAnniGreaterFlag事件
  useEffect(() => {
    if (preOrAfter === 'after') {
      if (day >= 3) {
        // 如果有intro，需要显示Event details，所以不隐藏容器 (false)
        // 如果没有intro，应该隐藏整个容器 (true)
        const shouldHide = !intro;
        onChangeAfterAnniGreaterFlag?.(shouldHide);
      } else {
        // day < 3时，显示倒计时，不隐藏容器
        onChangeAfterAnniGreaterFlag?.(false);
      }
    }
  }, [day, preOrAfter, intro, onChangeAfterAnniGreaterFlag]);

  // 监听timeLeft变化，触发倒计时结束事件
  useEffect(() => {
    // 只有在初始化完成后且倒计时结束时才触发回调
    if (hasInitializedRef.current && timeLeft <= 0) {
      onCountDownEnd?.();
    }
  }, [timeLeft, onCountDownEnd]);

  // 初始化倒计时
  useEffect(() => {
    // 清理之前的定时器
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // 初始化时记录初始天数
    const initialDayNum = Math.floor(time / (60 * 60 * 24));
    setLastDayBeforeEnd(initialDayNum);

    // 标记组件已经初始化
    hasInitializedRef.current = true;

    handleCountTime(time);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [time]);

  // 打开说明弹窗（仅移动端）
  const openIntroPop = () => {
    if (!intro) return;

    if (deviceType === EPlatform.WAP) {
      // 移动端使用Popup
      setShowIntro(true);
    }
    // PC端已经由Tooltip组件自动处理hover行为，不需要手动控制
  };

  // 渲染预售倒计时
  const renderPreCountdown = () => {
    if (preOrAfter !== 'pre') return null;

    // 预售倒计时进行中
    if (timeLeft >= 0) {
      return (
        <div className="flex items-center justify-center">
          {/* WAP端：图标在前面 */}
          {intro && deviceType === EPlatform.WAP && (
            <QuestionOutlined
              className="mr-[4px] text-[12px] text-[#808080] cursor-pointer"
              onClick={openIntroPop}
            />
          )}
          <span className="whitespace-nowrap">{t('preSaleCountdown')}</span>
          <div className="w-[62px] flex">
            <span className="ml-[3px] flex items-center justify-end">{hr}:</span>
            <span className="flex items-center justify-end">{min}:</span>
            <span className="flex items-center justify-end">{sec}</span>
          </div>
          {/* PC端：图标在后面 */}
          {intro && deviceType === EPlatform.WEB && (
            <HelpTooltip title={intro} colorClass="text-[#808080]" />
          )}
        </div>
      );
    }

    // 预售模式倒计时结束后，如果有活动说明则显示Event details
    if (timeLeft < 0 && intro) {
      return (
        <div className="flex items-center justify-center">
          {/* WAP端：图标在前面 */}
          {deviceType === EPlatform.WAP && (
            <QuestionOutlined
              className="mr-[4px] text-[12px] text-[#282828] cursor-pointer"
              onClick={openIntroPop}
            />
          )}
          <span className={`whitespace-nowrap text-[#282828]}`}>{t('eventDetails')}</span>
          {/* PC端：图标在后面 */}
          {deviceType === EPlatform.WEB && (
            <HelpTooltip title={intro} colorClass="text-[#282828]" />
          )}
        </div>
      );
    }

    return null;
  };

  // 渲染结束倒计时
  const renderAfterCountdown = () => {
    if (preOrAfter !== 'after') return null;

    // 活动结束模式，天数>=3时且有intro时，显示Event details
    if (typeDownClassAfterEnd === 'GreaterThanThree' && intro) {
      return (
        <div className="flex items-center justify-center">
          {/* WAP端：图标在前面 */}
          {deviceType === EPlatform.WAP && (
            <QuestionOutlined
              className="mr-[4px] text-[12px] text-[#282828] cursor-pointer"
              onClick={openIntroPop}
            />
          )}
          <span className={`whitespace-nowrap text-[#282828]}`}>{t('eventDetails')}</span>
          {/* PC端：图标在后面 */}
          {deviceType === EPlatform.WEB && (
            <HelpTooltip title={intro} colorClass="text-[#282828]" />
          )}
        </div>
      );
    }

    // 活动结束模式，天数>=3时但无intro时，显示空内容（外层容器会被隐藏）
    if (typeDownClassAfterEnd === 'GreaterThanThree' && !intro) {
      return <span />;
    }

    // 活动结束模式，天数<3时，显示倒计时
    return (
      <div className="flex items-center justify-center">
        {/* WAP端：图标在前面 */}
        {intro && deviceType === EPlatform.WAP && (
          <QuestionOutlined
            className="mr-[4px] text-[12px] md:text-[14px] text-[#808080] cursor-pointer"
            onClick={openIntroPop}
          />
        )}
        <span className="whitespace-nowrap">{t('endedIn')}</span>
        <div className="w-[62px] flex">
          <span className="ml-[3px] flex items-center justify-end">{hr}:</span>
          <span className="flex items-center justify-end">{min}:</span>
          <span className="flex items-center justify-end">{sec}</span>
        </div>
        {/* PC端：图标在后面 */}
        {intro && deviceType === EPlatform.WEB && (
          <HelpTooltip title={intro} colorClass="text-[#808080]" />
        )}
      </div>
    );
  };

  return (
    <>
      <div className="font-br flex items-center justify-center text-[11px] md:text-[14px] font-normal transform">
        {renderPreCountdown()}
        {renderAfterCountdown()}
      </div>

      {/* 移动端 Popup 说明 */}
      {intro && deviceType === EPlatform.WAP && (
        <Popup
          open={showIntro}
          onClose={() => setShowIntro(false)}
          position="bottom"
          title=""
          sx={{
            '& .popup-title': {
              borderBottom: 'none',
              padding: '10px',
            },
            '& .popup-content': {
              padding: '0 0',
            },
          }}
        >
          <div className="text-primary-black text-[14px] leading-[20px] text-left py-[20px] max-h-[50vh] overflow-y-scroll">
            <pre className="px-[22px] m-0 font-br leading-[19px] text-[14px] break-words whitespace-pre-wrap">
              {intro}
            </pre>
          </div>
        </Popup>
      )}
    </>
  );
}
